import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetSchoolClassesQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import { UsageBadge, EmptyState, DemoBanner } from '../../components/school/SchoolUi'

const SchoolClasses: React.FC = () => {
  const { t } = useTranslation('school')
  const { filters } = useSchoolFilters()
  const { data, isLoading } = useGetSchoolClassesQuery(filters)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('classes.title')}</h1>
        <p className="text-gray-600">{t('classes.subtitle')}</p>
      </div>
      {data?.isDemo ? <DemoBanner label={t('common.demoBanner')} /> : null}
      <DateRangeBar showClassFilter={false} />
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('common.loading')}</div>
        ) : !(data?.classes || []).length ? (
          <EmptyState title={t('classes.empty')} hint={t('classes.emptyHint')} />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('classes.columns.class')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('classes.columns.teacher')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('classes.columns.active')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('classes.columns.aiSessions')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('classes.columns.usage')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.classes.map((row: any) => {
                const total = row.studentCount || 0
                const active = row.activeStudents || 0
                const pct = total ? Math.round((active / total) * 1000) / 10 : 0
                return (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <Link to={`/pending/classes/${row.id}`} className="text-blue-600 hover:underline font-medium">{row.name}</Link>
                    </td>
                    <td className="px-4 py-3 text-sm">{row.teacherName || t('common.na')}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {active}<span className="text-gray-400 font-normal"> / {total}</span>
                      <span className="ml-2 text-xs font-normal text-gray-500">{pct}%</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{row.aiSessions}</td>
                    <td className="px-4 py-3 text-sm">
                      <UsageBadge status={row.usageStatus} label={t(`teachers.usage.${row.usageStatus}`)} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default SchoolClasses
